import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { TbUsers, TbCash, TbCreditCard, TbShoppingCart } from 'react-icons/tb';
import { RootState } from '../../../redux/reducers';
import { BalanceStats, BankTransactionWithParticipation } from '../../../types/bankAccount';
import * as bankAccountService from '../../../services/bankAccountService';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
} from '../../components/tables/BaseTable';
import PageHeader from '../../components/misc/PageHeader';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import connectionService from '../../../services/connectionService';
import { formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';
import { checkMandateStatusForUser } from '../../../services/merchant.marketplace.service';
import { C2BOrderMetaOutDto } from '../../../services/model/marketplace.model';
import { getOrdersForUserMetadata } from '../../../services/orders.marketplace.service';

type BalanceViewModel = [string, { personal: BalanceStats; contribution: BalanceStats }];

// GL6572120000072123
interface FinancialMemberBalancePageProps {
  hideHeader?: boolean;
}

export default function FinancialMemberBalancePage({
  hideHeader = false,
}: FinancialMemberBalancePageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.memberBalance' });
  const navigate = useNavigate();
  const connections = useSelector((state: RootState) => state.connections).filter(
    (c) => c.type === ConnectionType.B2C_CONNECTION,
  );
  const [transactions, setTransactions] = React.useState<
    BankTransactionWithParticipation[] | null
  >();
  const [contributionBalances, setContributionBalances] = React.useState<Record<
    number,
    BalanceStats
  > | null>();
  const [balances, setBalances] = React.useState<BalanceViewModel[]>([]);
  const [mandateStatus, setMandateStatus] = React.useState<Record<number, boolean>>({});
  const [orderMetadata, setOrderMetadata] = React.useState<Record<number, C2BOrderMetaOutDto>>({});
  const business = useSelectedBusiness();

  useEffect(() => {
    if (business) {
      Promise.all([
        bankAccountService.getAllTransactions(),
        bankAccountService.getContributionBalancesForConnections(),
        connectionService.getConnections(),
      ])
        .then(([tns, bs, cs]) => {
          setTransactions(tns);
          setContributionBalances(bs);
          // Check mandate status for each connection
          cs.forEach((c) => {
            checkMandateStatusForUser(c.userId ?? -1)
              .then((status) => {
                setMandateStatus((prev) => ({ ...prev, [c.id]: status }));
              })
              .catch(() => {
                setMandateStatus((prev) => ({ ...prev, [c.id]: false }));
              });
            getOrdersForUserMetadata(c.userId ?? -1)
              .then((meta) => {
                setOrderMetadata((prev) => ({ ...prev, [c.id]: meta }));
              })
              .catch(() => {});
          });
        })
        .catch(() => setTransactions(null));
    }
  }, [business]);

  useEffect(() => {
    if (transactions && contributionBalances) {
      // TODO ideally, we can do this in the backend
      const aggregated = getPersonalBalances(transactions);
      const declaredAmounts = getDeclarationAmounts(transactions);
      const missingConnections = new Set(connections.map((c) => c.id.toString()));
      aggregated.forEach(([cId]) => missingConnections.delete(cId));

      const emptyValues: [string, BalanceStats][] = Array.from(missingConnections).map((id) => [
        id,
        { ...DEFAULT_BALANCE_STATS },
      ]);
      setBalances(
        aggregated
          .concat(emptyValues)
          .map<BalanceViewModel>(([k, { income, balance, expenses }]) => [
            k,
            {
              personal: {
                expenses,
                income: income - (declaredAmounts?.[+k] ?? 0), // minus because an expense is a negative amount
                balance: balance - (declaredAmounts?.[+k] ?? 0),
              },
              contribution: { ...(contributionBalances[+k] ?? DEFAULT_BALANCE_STATS) },
            },
          ])
          .sort(([a], [b]) => +a - +b),
      );
    }
  }, [transactions, contributionBalances]);

  const handleRowClick = (connectionId: string) => {
    navigate(`/financial/member/${connectionId}`);
  };

  // Calculate overall statistics
  const overallStats = React.useMemo(() => {
    if (!balances.length) return null;

    const totalPersonalBalance = balances.reduce(
      (sum, [, stats]) => sum + stats.personal.balance,
      0,
    );
    const totalContributionBalance = balances.reduce(
      (sum, [, stats]) => sum + stats.contribution.balance,
      0,
    );

    // Mock data for direct debit and sales orders
    const totalDirectDebitActive = mandateStatus
      ? Object.values(mandateStatus).filter((status) => status).length
      : 0;
    const totalSalesOrders = Object.values(orderMetadata).reduce(
      (sum, meta) => sum + (meta.totalOrders ?? 0),
      0,
    );
    return {
      totalMembers: balances.length,
      totalPersonalBalance,
      totalContributionBalance,
      totalDirectDebitActive,
      totalSalesOrders,
    };
  }, [balances]);

  return (
    <div className="flex flex-col">
      {!hideHeader && <PageHeader title={t('title')} subtitle={t('subtitle')} />}
      {!transactions && <EmptyScreenView />}

      {transactions && overallStats && (
        <>
          {/* Overall Statistics */}
          <div className="mb-8">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">{t('overallStats.title')}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TbUsers className="h-6 w-6 text-primary-300" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      {t('overallStats.totalMembers')}
                    </p>
                    <p className="text-xl font-bold text-gray-900">{overallStats.totalMembers}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TbCash className="h-6 w-6 text-success" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      {t('overallStats.totalPersonalBalance')}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatEuroCentsWithSign(overallStats.totalPersonalBalance, i18n.language)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TbCash className="h-6 w-6 text-primary-300" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      {t('overallStats.totalContributionBalance')}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatEuroCentsWithSign(
                        overallStats.totalContributionBalance,
                        i18n.language,
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TbCreditCard className="h-6 w-6 text-secondary" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      {t('overallStats.totalDirectDebitActive')}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {overallStats.totalDirectDebitActive}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TbShoppingCart className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      {t('overallStats.totalSalesOrders')}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {overallStats.totalSalesOrders}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Updated Table */}
          <div className="relative w-full overflow-x-auto">
            <BaseTable>
              <BaseTableHeader>
                <BaseTableHeaderRow>
                  <th className="text-left">{t('table.member')}</th>
                  <th className="text-center">{t('table.personal')}</th>
                  <th className="text-center">{t('table.contribution')}</th>
                  <th className="text-center">{t('table.directDebit')}</th>
                  <th className="text-center">{t('table.salesOrders')}</th>
                </BaseTableHeaderRow>
              </BaseTableHeader>
              <BaseTableBody>
                {balances.map(([connectionId, memberStats]) => (
                  <BaseTableDataRow
                    key={connectionId}
                    className="group cursor-pointer transition-all hover:bg-secondary-200"
                    onClick={() => handleRowClick(connectionId)}>
                    <td className="py-3 text-left">
                      {connections.find((c) => +c.id === +connectionId)?.fields.FIRST_NAME}{' '}
                      {connections.find((c) => +c.id === +connectionId)?.fields.LAST_NAME}
                    </td>
                    <td className="py-3 text-center">
                      <span
                        className={`font-medium ${
                          memberStats.personal.balance >= 0 ? 'text-success' : 'text-danger'
                        }`}>
                        {formatEuroCentsWithSign(memberStats.personal.balance, i18n.language)}
                      </span>
                    </td>
                    <td className="py-3 text-center">
                      <span
                        className={`font-medium ${
                          memberStats.contribution.balance >= 0 ? 'text-success' : 'text-danger'
                        }`}>
                        {formatEuroCentsWithSign(memberStats.contribution.balance, i18n.language)}
                      </span>
                    </td>
                    <td className="py-3 text-center">
                      {(() => {
                        const status = mandateStatus[+connectionId] ? 'active' : '-';
                        return status === 'active' ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-success">
                            {status}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        );
                      })()}
                    </td>
                    <td className="py-3 text-center">
                      <span className="font-medium text-gray-900">
                        {orderMetadata[+connectionId]?.totalOrders ?? '-'}
                      </span>
                    </td>
                  </BaseTableDataRow>
                ))}
              </BaseTableBody>
            </BaseTable>
          </div>
        </>
      )}
    </div>
  );
}

const DEFAULT_BALANCE_STATS = {
  income: 0,
  expenses: 0,
  balance: 0,
} as const;

function getPersonalBalances(tns: BankTransactionWithParticipation[]) {
  return _.chain(tns)
    .flatMap((t) => t.participations)
    .filter((t) => !!t)
    .groupBy((t) => +t!.connectionId)
    .mapValues((ps) => ({
      income: ps.filter((p) => p!.amount > 0).reduce((sum, p) => sum + p!.amount, 0),
      expenses: ps.filter((p) => p!.amount < 0).reduce((sum, p) => sum + p!.amount, 0),
      balance: ps.reduce((sum, p) => sum + p!.amount, 0),
    }))
    .entries()
    .value();
}

function getDeclarationAmounts(tns: BankTransactionWithParticipation[]): Record<number, number> {
  return _.chain(tns)
    .filter((tn) => !!tn.payerId)
    .groupBy((tn) => tn.payerId)
    .mapValues((xs) => xs.reduce((sum, x) => sum + x.amount, 0))
    .value();
}
